/**
 * @file inventory-replenishment-report.tsx
 * @module app/modules/uProcure/inventory-replenishment/inventory-replenishment-report
 * @description Inventory Replenishment report: one row per product and quantity type for the
 *   selected warehouse and a date range of up to 366 days, with what to procure (Order Needed),
 *   row flags, filters, sorting and a one-call Excel export. Data: GET _product/reports/inventory-replenishment.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { reports } from "../../../../api/apiEndPoints";
import { KTSVG } from "../../../../_admin/helpers";
import SimplePagination from "../../../../Global/simple-pagination";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import ReportDateRange from "./report-date-range";
import SyncRunSummary, { RunInfo } from "./sync-run-summary";
import { rangeError } from "./report-date-presets";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import Method from "../../../../utils/methods";
import {
  InventoryReplenishmentReport,
  Download,
  View,
  CartonWithDozens,
  Dozen,
  Piece,
  CartonWithPieces,
} from "../../../../utils/constants";

const PAGE_SIZE = 20;
/** how often a running Sync now is checked */
const SYNC_POLL_MS = 4000;

const QTY_TYPES = [
  { value: CartonWithDozens, label: "Carton with Dozens" },
  { value: Dozen, label: "Dozen with Pieces" },
  { value: Piece, label: "Piece" },
  { value: CartonWithPieces, label: "Carton with Pieces" },
];
/** Short unit names used next to quantities. */
const UNIT_SHORT: Record<number, string> = { 1: "CWD", 2: "DWP", 3: "P", 4: "CWP" };

const qtyLabel = (type: number) =>
  QTY_TYPES.find((q) => q.value === type)?.label ?? "N/A";

/** Flag codes from the API: label, badge colour and what it means. */
const FLAGS: Record<string, { label: string; color: string; help: string }> = {
  INACTIVE: { label: "Inactive", color: "secondary", help: "No sales in the range and out of stock on every counted day" },
  NO_SALES: { label: "No sales", color: "secondary", help: "In stock during the range but nothing sold" },
  NOT_STOCKED: { label: "Not stocked", color: "secondary", help: "Not stocked in this warehouse before the end of the range" },
  NEW_IN_RANGE: { label: "New in range", color: "info", help: "First stocked during the range, so fewer days were counted" },
  OVERSOLD: { label: "Oversold", color: "danger", help: "Blocked stock is more than warehouse stock" },
  DRAFT_PO_OFF: { label: "Draft PO off", color: "dark", help: "Draft PO is switched off in the Price Catalog" },
  NO_SUPPLIER: { label: "No supplier", color: "danger", help: "Order needed but no completed PO: supplier and quantity type unknown" },
  NO_COST_PRICE: { label: "No cost price", color: "danger", help: "Order needed but no active cost price in the Price Catalog" },
  DEFAULT_LEAD_TIME: { label: "Default lead time", color: "warning", help: "Supplier lead time missing or above 10 days: Settings default used" },
  DEFAULT_DOIH: { label: "Default DOIH", color: "warning", help: "No DOIH in the Price Catalog: lead time + Settings extra days used" },
  OLD_PENDING_PO: { label: "Old Pending PO", color: "danger", help: "A Pending PO older than Settings \"Old PO after (days)\": not subtracted from Order Needed; a draft PO with this product needs super admin approval" },
  OLD_APPROVED_PO: { label: "Old Approved PO", color: "warning", help: "An Approved PO with quantity pending, older than Settings \"Old PO after (days)\": not subtracted from Order Needed" },
  OLD_IN_PROCESS_PO: { label: "Old In Process PO", color: "warning", help: "An In Process PO with quantity pending, older than Settings \"Old PO after (days)\": not subtracted from Order Needed" },
  QTY_TYPE_NOT_FROM_PO: { label: "Qty type not from PO", color: "warning", help: "No completed PO: shown in the qty type sold most (or most stocked)" },
  UNIT_CONVERSION_MISSING: { label: "Unit conversion missing", color: "danger", help: "A selling qty type has no pack size and is left out of the totals" },
  SPIKE_DAYS_TRIMMED: { label: "Spike days trimmed", color: "info", help: "One or more days sold far above a normal day (over 3x the median selling day); those days count as a normal day for ordering" },
  DEMAND_CAPPED: { label: "Demand capped", color: "warning", help: "Sales rose more than Settings \"Max demand rise\" over the previous period / last year: ordering uses the capped demand, not the spike" },
  NO_DEMAND_HISTORY: { label: "No sales history", color: "secondary", help: "No previous period or last year sales to compare (new product or not sold before): ordering uses this period's rate" },
};

/** Badge colours with dark text (contrast 5:1 or more) instead of the theme's pale light-badges. */
const BADGE_COLORS: Record<string, { color: string; backgroundColor: string; borderColor: string }> = {
  warning: { color: "#7A4100", backgroundColor: "#FFF1D6", borderColor: "#F0B95B" },
  danger: { color: "#9B1C1C", backgroundColor: "#FDE8E8", borderColor: "#F1A8A8" },
  success: { color: "#0E5E34", backgroundColor: "#E3F6EA", borderColor: "#8FD3AA" },
  primary: { color: "#0B4F9C", backgroundColor: "#E5F0FF", borderColor: "#9CC3F5" },
  info: { color: "#1D4E89", backgroundColor: "#E6F0FB", borderColor: "#A9C6EA" },
  secondary: { color: "#33384D", backgroundColor: "#EEF0F4", borderColor: "#C9CDD8" },
  dark: { color: "#FFFFFF", backgroundColor: "#3F4254", borderColor: "#3F4254" },
  light: { color: "#4B5066", backgroundColor: "#F5F6F8", borderColor: "#DADDE5" },
};
const badgeStyle = (tone: string) => ({ ...(BADGE_COLORS[tone] ?? BADGE_COLORS.secondary), border: "1px solid", fontSize: 12, lineHeight: 1.4 });

/** old-PO flags and the PO status each one lists */
const OLD_PO_FLAG_STATUS: Record<string, string> = { OLD_PENDING_PO: "Pending", OLD_APPROVED_PO: "Approved", OLD_IN_PROCESS_PO: "In Process" };

interface OpenPo {
  refKey: string;
  status: string;
  ageDays: number;
  pending: number;
  /** within the open PO age limit, so subtracted from Order Needed */
  counted: boolean;
}
type DraftStatus = "match" | "differs" | "none" | "not_needed";
const DRAFT_STATUS: Record<DraftStatus, { label: string; color: string }> = {
  match: { label: "Matches", color: "success" },
  differs: { label: "Differs", color: "warning" },
  none: { label: "Not on a draft", color: "secondary" },
  not_needed: { label: "Nothing to order", color: "light" },
};
const DRAFT_STATUS_OPTIONS = [
  { value: "none", label: "Not on a draft PO" },
  { value: "differs", label: "Differs from the draft PO" },
  { value: "none,differs", label: "Not on a draft or differs" },
  { value: "match", label: "Matches the draft PO" },
];
interface ReplenishmentRow {
  variant: string;
  type: number;
  title: string;
  refKey?: string;
  category?: string;
  brand?: string;
  blockedStock: number;
  totalWarehouseStock: number;
  qtySold: number;
  salesValue: number;
  ats: number;
  runRate: number;
  /** per day in the period before the range / same dates last year (null: not stocked then) */
  previousRate: number | null;
  lastYearRate: number | null;
  /** demand per day used for Order Needed (spike-safe) */
  forecastRate: number;
  spikeDaysTrimmed: number;
  stockCoverDays: number | null;
  oosDays: number;
  daysInStock: number;
  revenueLoss: number;
  lastRestockDate: string | null;
  lastRestockQty: number;
  openPoQty: number;
  openPoValue: number;
  openPos: OpenPo[];
  /** quantity on open POs too old to count */
  oldOpenPoQty: number;
  /** the product's line on an open draft PO of the warehouse */
  onDraft: { draftId: string; refKey: string; quantity: number; quantityType: number } | null;
  draftStatus: DraftStatus;
  draftNote: string;
  doih: number;
  leadTimeDays: number;
  orderNeeded: number;
  /** supplier MOQ in the row unit (null = none set) */
  moq: number | null;
  /** Order Needed rounded to the nearest 10 (below 10: as is), before the MOQ; older responses lack it */
  roundedOrder?: number;
  /** Order Needed rounded off, then raised to the MOQ: what the draft PO will order */
  suggestedOrder: number;
  vendorName: string;
  poStockCount: number;
  poPurchaseAmount: number;
  costPrice: number | null;
  draftPOEnabled: boolean;
  /** selling qty types included, with PO units per selling unit */
  parts: { type: number; factor: number | null; totalWarehouseStock: number; blockedStock: number; qtySold: number }[];
  flags: string[];
}
interface Option {
  value: string;
  label: string;
}
interface SyncStatus {
  /** the draft PO job's report range (Settings: Report Range days, ending yesterday) */
  jobRange: { startDay: string; endDay: string; totalDays: number };
  /** the report shows the job's range, so `needed` is known */
  sameRange: boolean;
  /** products whose draft line Sync now would add, update or remove (null = other range) */
  needed: number | null;
  lastRun: { finishedAt: string | null; trigger: "daily" | "manual"; by: string } | null;
  /** latest run, also while it is running */
  current?: RunInfo | null;
  canSync: boolean;
}
interface ReplenishmentResponse {
  range: { startDay: string; endDay: string; totalDays: number };
  total: number;
  records: ReplenishmentRow[];
  summary: {
    rows: number;
    qtySold: number;
    salesValue: number;
    revenueLoss: number;
    orderNeededRows: number;
    flags: Record<string, number>;
    draftStatus?: Record<string, number>;
  };
  /** user may open draft POs (links in the On Draft PO column) */
  canOpenDrafts?: boolean;
  sync?: SyncStatus;
  /** Settings "Old PO after (days)" */
  oldPoDays?: number;
  options: {
    categories: Option[];
    subCategories: Option[];
    groupCategories: Option[];
    brands: Option[];
  };
}

/** Columns in table and Excel order. */
const COLUMNS: { key: string; label: string; sortable?: boolean; excel: (r: ReplenishmentRow) => string | number }[] = [
  { key: "title", label: "Product", sortable: true, excel: (r) => r.title },
  { key: "type", label: "Qty Type (PO)", sortable: true, excel: (r) => qtyLabel(r.type) },
  { key: "blockedStock", label: "Blocked Stock", sortable: true, excel: (r) => r.blockedStock },
  { key: "totalWarehouseStock", label: "Total Warehouse Stock", sortable: true, excel: (r) => r.totalWarehouseStock },
  { key: "qtySold", label: "Qty Sold", sortable: true, excel: (r) => r.qtySold },
  { key: "salesValue", label: "Sales Value", sortable: true, excel: (r) => r.salesValue },
  { key: "ats", label: "ATS", sortable: true, excel: (r) => r.ats },
  { key: "runRate", label: "Run Rate / Day", sortable: true, excel: (r) => r.runRate },
  { key: "previousRate", label: "Previous Period / Day", sortable: true, excel: (r) => r.previousRate ?? "Not stocked" },
  { key: "lastYearRate", label: "Same Period Last Year / Day", sortable: true, excel: (r) => r.lastYearRate ?? "Not stocked" },
  { key: "forecastRate", label: "Demand Used / Day", sortable: true, excel: (r) => r.forecastRate },
  { key: "stockCoverDays", label: "Stock Cover Days", sortable: true, excel: (r) => r.stockCoverDays ?? "No sales" },
  { key: "oosDays", label: "OOS Days", sortable: true, excel: (r) => r.oosDays },
  { key: "daysInStock", label: "Days in Stock", sortable: true, excel: (r) => r.daysInStock },
  { key: "revenueLoss", label: "Revenue Loss", sortable: true, excel: (r) => r.revenueLoss },
  { key: "lastRestockDate", label: "Last Restock Date", sortable: true, excel: (r) => r.lastRestockDate ?? "" },
  { key: "lastRestockQty", label: "Last Restock Qty", sortable: true, excel: (r) => r.lastRestockQty },
  { key: "openPoQty", label: "Open PO Qty", sortable: true, excel: (r) => r.openPoQty },
  { key: "openPoValue", label: "Open PO Value (TSh)", sortable: true, excel: (r) => r.openPoValue },
  { key: "leadTimeDays", label: "Lead Time (days)", sortable: true, excel: (r) => r.leadTimeDays },
  { key: "doih", label: "DOIH (days)", sortable: true, excel: (r) => r.doih },
  { key: "orderNeeded", label: "Order Needed", sortable: true, excel: (r) => r.orderNeeded },
  { key: "orderNeededUnit", label: "Order Needed Unit (last PO qty type)", excel: (r) => qtyLabel(r.type) },
  { key: "moq", label: "Supplier MOQ", sortable: true, excel: (r) => r.moq ?? "" },
  { key: "suggestedOrder", label: "Suggested Order (rounded, with MOQ)", sortable: true, excel: (r) => r.suggestedOrder },
  {
    key: "draftStatus",
    label: "On Draft PO",
    sortable: true,
    excel: (r) =>
      r.onDraft
        ? `${r.onDraft.refKey}: ${r.onDraft.quantity} ${UNIT_SHORT[r.onDraft.quantityType] ?? ""} (${DRAFT_STATUS[r.draftStatus].label}${r.draftNote ? `: ${r.draftNote}` : ""})`
        : r.draftStatus === "none"
          ? `Not on a draft: ${r.draftNote}`
          : "",
  },
  { key: "vendorName", label: "Vendor Name (PO)", sortable: true, excel: (r) => r.vendorName },
  { key: "poStockCount", label: "Last PO Qty", sortable: true, excel: (r) => r.poStockCount },
  { key: "poPurchaseAmount", label: "Purchase Amount (PO)", sortable: true, excel: (r) => r.poPurchaseAmount },
  {
    key: "flags",
    label: "Flags",
    excel: (r) =>
      r.flags
        .map((f) =>
          OLD_PO_FLAG_STATUS[f]
            ? `${FLAGS[f]?.label ?? f}: ${r.openPos
                .filter((p) => !p.counted && p.status === OLD_PO_FLAG_STATUS[f])
                .map((p) => `${p.refKey} (${p.ageDays} days)`)
                .join(" ")}`
            : FLAGS[f]?.label ?? f
        )
        .join(", "),
  },
];

const fmt = (n: number | null | undefined, digits = 0) =>
  n === null || n === undefined
    ? "-"
    : Number(n).toLocaleString("en-US", { maximumFractionDigits: digits });

/** e.g. "Piece: stock 5, sold 83 (1 = 0.25 Carton with Pieces)" for each other selling unit */
const partsText = (r: ReplenishmentRow) =>
  r.parts
    .filter((p) => p.type !== r.type)
    .map(
      (p) =>
        `${qtyLabel(p.type)}: stock ${p.totalWarehouseStock}, blocked ${p.blockedStock}, sold ${p.qtySold}` +
        (p.factor === null ? " (no pack size, not included)" : ` (1 = ${fmt(p.factor, 4)} ${qtyLabel(r.type)})`)
    )
    .join("; ");

const ymd = (d: Date) => Method.convertDateToFormat(d, "YYYY-MM-DD");

// same as the automatic draft PO job (Settings: Report Range, default 30 days, ending yesterday)
const defaultRange = () => {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start, end };
};

const InventoryReplenishmentReportPage = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canDownload = Method.hasPermission(InventoryReplenishmentReport, Download, currentUser);

  const [startDate, setStartDate] = useState<Date | null>(defaultRange().start);
  const [endDate, setEndDate] = useState<Date | null>(defaultRange().end);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<Option | null>(null);
  const [subCategory, setSubCategory] = useState<Option | null>(null);
  const [groupCategory, setGroupCategory] = useState<Option | null>(null);
  const [brand, setBrand] = useState<Option | null>(null);
  const [qtyType, setQtyType] = useState<{ value: number; label: string } | null>(null);
  const [flag, setFlag] = useState<Option | null>(null);
  const [draftFilter, setDraftFilter] = useState<Option | null>(null);
  const navigate = useNavigate();
  // the report opens on what to procure; untick to see every product
  const [orderNeededOnly, setOrderNeededOnly] = useState(true);
  // filters only take effect when "Apply Filters" is clicked (sorting and paging apply at once)
  const [applied, setApplied] = useState<Record<string, unknown>>(() => {
    const d = defaultRange();
    return { startDate: ymd(d.start), endDate: ymd(d.end), orderNeededOnly: true };
  });
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ReplenishmentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // bumped after Sync now so the report reloads with fresh figures
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!Method.hasPermission(InventoryReplenishmentReport, View, currentUser)) {
      window.history.back();
    }
  }, []);

  /** Filters as currently chosen on screen (not yet applied). */
  const chosenFilters = (): Record<string, unknown> => {
    const q: Record<string, unknown> = {};
    if (startDate && endDate) {
      q.startDate = ymd(startDate);
      q.endDate = ymd(endDate);
    }
    if (search.trim()) q.searchTerm = search.trim();
    if (category) q.category = category.value;
    if (subCategory) q.subCategory = subCategory.value;
    if (groupCategory) q.groupCategory = groupCategory.value;
    if (brand) q.brand = brand.value;
    if (qtyType) q.qtyTypes = String(qtyType.value);
    if (flag) q.flags = flag.value;
    if (draftFilter) q.draftStatus = draftFilter.value;
    if (orderNeededOnly) q.orderNeededOnly = true;
    return q;
  };
  const pendingChanges = JSON.stringify(chosenFilters()) !== JSON.stringify(applied);

  const applyFilters = () => {
    const invalid = rangeError(startDate, endDate);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setApplied(chosenFilters());
  };

  /** Shows the report for the dates Sync now uses (Settings: Report range), so both agree. */
  const showSyncDates = (r: { startDay: string; endDay: string }) => {
    const start = new Date(`${r.startDay}T12:00:00`);
    const end = new Date(`${r.endDay}T12:00:00`);
    setStartDate(start);
    setEndDate(end);
    setApplied({ ...chosenFilters(), startDate: r.startDay, endDate: r.endDay });
  };

  const resetFilters = () => {
    const d = defaultRange();
    setStartDate(d.start);
    setEndDate(d.end);
    setSearch("");
    setCategory(null);
    setSubCategory(null);
    setGroupCategory(null);
    setBrand(null);
    setQtyType(null);
    setFlag(null);
    setDraftFilter(null);
    setOrderNeededOnly(true);
    setApplied({ startDate: ymd(d.start), endDate: ymd(d.end), orderNeededOnly: true });
  };

  const query = useMemo(() => {
    if (!selectedWarehouse?.value || !applied.startDate) return null;
    const q: Record<string, unknown> = { warehouse: selectedWarehouse.value, ...applied };
    if (sortKey) {
      q.sortKey = sortKey;
      q.sortOrder = sortOrder;
    }
    return q;
  }, [selectedWarehouse?.value, applied, sortKey, sortOrder]);

  const callApi = async (params: Record<string, unknown>): Promise<ReplenishmentResponse | null> => {
    const api = new APICallService(reports.inventoryReplenishmentReport, params, "", "", "", "", InventoryReplenishmentReport, selectedWarehouse?.value);
    const res = await api.callAPI();
    return res && Array.isArray(res.records) ? (res as ReplenishmentResponse) : null;
  };

  // back to page 1 whenever the filters change
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    if (!query) return;
    let stale = false;
    (async () => {
      setLoading(true);
      const res = await callApi({ ...query, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, ...(reloadKey ? { refresh: true } : {}) });
      if (!stale) {
        if (res) setData(res);
        setLoading(false);
      }
    })();
    return () => {
      stale = true;
    };
  }, [query, page, reloadKey]);

  /**
   * Follows a running sync with one light status request every few seconds (nothing is polled
   * otherwise). When it ends (done, failed, or cancelled and rolled back) the report reloads, so
   * the new figures and the run summary show without refreshing the page.
   */
  const pollSync = async (warehouse: string) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setSyncing(true);
    try {
      for (;;) {
        await new Promise((r) => setTimeout(r, SYNC_POLL_MS));
        // stop when the user switched warehouse or left the page
        if (warehouseRef.current !== warehouse) return;
        const api = new APICallService(reports.inventoryReplenishmentSyncStatus, { warehouse }, "", "", "", "", InventoryReplenishmentReport, warehouse);
        const res = (await api.callAPI()) as { current: RunInfo | null } | null;
        const run = res?.current;
        // a cancelled run is finished once its rollback is done
        if (!run || run.status === "running" || (run.status === "cancelled" && !run.rolledBack)) continue;
        if (run.status === "done") toast.success("Draft POs synced");
        else if (run.status === "cancelled") toast.info(`Sync cancelled: ${run.restored ?? 0} draft(s) put back as they were`);
        else toast.error(run.error || "Sync failed");
        setReloadKey((k) => k + 1);
        return;
      }
    } finally {
      pollingRef.current = false;
      setSyncing(false);
      setCancelling(false);
    }
  };

  /** Sync now: starts the draft PO job for this warehouse in the background and follows it. */
  const syncNow = async () => {
    const warehouse = selectedWarehouse?.value;
    if (!warehouse) return;
    setSyncing(true);
    const api = new APICallService(reports.inventoryReplenishmentSync, { warehouse }, "", "", "", "", InventoryReplenishmentReport, warehouse);
    const res = (await api.callAPI()) as { started?: boolean } | null;
    if (res?.started) pollSync(warehouse);
    else setSyncing(false);
  };

  /** Cancel sync: stops the run and rolls back what it changed; the poll shows when that is done. */
  const cancelSync = async () => {
    const warehouse = selectedWarehouse?.value;
    if (!warehouse) return;
    setCancelling(true);
    const api = new APICallService(reports.inventoryReplenishmentSyncCancel, { warehouse }, "", "", "", "", InventoryReplenishmentReport, warehouse);
    const res = (await api.callAPI()) as { cancelled?: number } | null;
    if (!res) setCancelling(false);
    else pollSync(warehouse);
  };

  // a sync already running when the page opens (or after a reload): follow it
  const warehouseRef = useRef<string | undefined>(selectedWarehouse?.value);
  const pollingRef = useRef(false);
  useEffect(() => {
    warehouseRef.current = selectedWarehouse?.value;
    return () => {
      warehouseRef.current = undefined;
    };
  }, [selectedWarehouse?.value]);
  useEffect(() => {
    const run = data?.sync?.current;
    if (run && selectedWarehouse?.value && (run.status === "running" || (run.status === "cancelled" && !run.rolledBack))) pollSync(selectedWarehouse.value);
  }, [data?.sync?.current?.status, data?.sync?.current?.rolledBack]);

  const fmtTime = (iso: string | null) => (iso ? Method.convertDateToFormat(new Date(iso), "DD/MM/YYYY HH:mm") : "-");


  const handleSort = (key: string) => {
    if (sortKey === key) setSortOrder(sortOrder === 1 ? -1 : 1);
    else {
      setSortKey(key);
      setSortOrder(-1);
    }
  };

  const handleExport = async () => {
    if (!query) return;
    setExporting(true);
    try {
      const res = await callApi({ ...query, download: true });
      if (!res) return;
      const sheet = XLSX.utils.aoa_to_sheet([
        [`Inventory Replenishment Report - ${selectedWarehouse?.label ?? ""}`],
        [`Range: ${res.range.startDay} to ${res.range.endDay} (${res.range.totalDays} days)`],
        [],
        [...COLUMNS.map((c) => c.label), "Open POs", "Cost Price (Catalog)", "Sold In (converted to PO unit)"],
        ...res.records.map((r) => [
          ...COLUMNS.map((c) => c.excel(r)),
          r.openPos.map((p) => `${p.refKey} (${p.status}, ${p.ageDays}d, ${p.pending})`).join("; "),
          r.costPrice ?? "",
          partsText(r),
        ]),
      ]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Inventory Replenishment Report");
      XLSX.writeFile(book, `Inventory_Replenishment_Report_${res.range.startDay}_to_${res.range.endDay}.xlsx`);
    } catch (e) {
      toast.error("Could not download the Inventory Replenishment report");
    } finally {
      setExporting(false);
    }
  };

  const records = data?.records ?? [];
  const total = data?.total ?? 0;
  const flagOptions: Option[] = Object.entries(FLAGS).map(([value, f]) => ({
    value,
    label: `${f.label}${data?.summary.flags[value] ? ` (${data.summary.flags[value]})` : ""}`,
  }));

  return (
    <>
      <Row className="align-items-center g-3 mb-5">
        <Col>
          <h1 className="fs-22 fw-bolder mb-0">Inventory Replenishment Report</h1>
          {data && (
            <div className="fs-14 text-gray-600 mt-1">
              {data.range.startDay} to {data.range.endDay} ({data.range.totalDays} days)
            </div>
          )}
        </Col>
        {canDownload && (
          <Col xs="auto">
            <Button variant="primary" onClick={handleExport} disabled={exporting || loading || !total}>
              {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : (
                <KTSVG path="/media/icons/duotune/files/fil021.svg" className="svg-icon-3" />
              )}
              Download Excel
            </Button>
          </Col>
        )}
      </Row>

      <Card className="border bg-light mb-5">
        <Card.Body className="px-7 py-6">
          <Row className="g-4 align-items-end">
            <Col lg={6}>
              <label className="fs-13 fw-600 text-gray-700 mb-2">Search</label>
              <input
                type="text"
                className="form-control bg-white h-50px fs-14"
                placeholder="Product name or SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
            </Col>
            <Col lg={6}>
              <label className="fs-13 fw-600 text-gray-700 mb-2">Date range (up to 366 days)</label>
              <ReportDateRange
                start={startDate}
                end={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                disabled={loading}
              />
            </Col>
            {[
              { label: "Category", value: category, options: data?.options.categories ?? [], onChange: setCategory },
              { label: "Sub Category", value: subCategory, options: data?.options.subCategories ?? [], onChange: setSubCategory },
              { label: "Group Category", value: groupCategory, options: data?.options.groupCategories ?? [], onChange: setGroupCategory },
              { label: "Brand", value: brand, options: data?.options.brands ?? [], onChange: setBrand },
              { label: "Qty Type (PO)", value: qtyType, options: QTY_TYPES, onChange: setQtyType },
              { label: "Flag", value: flag, options: flagOptions, onChange: setFlag },
              { label: "Draft PO", value: draftFilter, options: DRAFT_STATUS_OPTIONS, onChange: setDraftFilter },
            ].map((f) => (
              <Col key={f.label} xl={4} md={6}>
                <label className="fs-13 fw-600 text-gray-700 mb-2">{f.label}</label>
                <CustomSelect
                  placeholder={`All`}
                  backgroundColor="#fff"
                  minHeight="50px"
                  value={f.value}
                  isClearable
                  options={f.options}
                  onChange={f.onChange}
                />
              </Col>
            ))}
            <Col xs={12}>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-4 pt-2">
                <label className="form-check form-check-custom form-check-solid fs-14 fw-600 mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={orderNeededOnly}
                    onChange={(e) => setOrderNeededOnly(e.target.checked)}
                  />
                  <span className="form-check-label text-dark">Only products to procure (Order Needed &gt; 0)</span>
                </label>
                <div className="d-flex align-items-center gap-3">
                  {pendingChanges && <span className="fs-13 text-warning fw-600">Filters changed: click Apply</span>}
                  <Button variant="light" onClick={resetFilters} disabled={loading}>
                    Reset
                  </Button>
                  <Button variant="primary" onClick={applyFilters} disabled={loading || !pendingChanges} style={{ minWidth: 150 }}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Applying...
                      </>
                    ) : (
                      "Apply Filters"
                    )}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {data?.sync && (
        <Card className={`border mb-5 ${data.sync.needed ? "border-warning" : ""}`}>
          <Card.Body className="py-4 px-6 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="fs-14">
              <div>
                <span className="fw-bold">Draft POs last synced:</span> {fmtTime(data.sync.lastRun?.finishedAt ?? null)}
                {data.sync.lastRun && (
                  <span className="text-gray-600">
                    {" "}
                    ({data.sync.lastRun.trigger === "manual" ? `Sync now by ${data.sync.lastRun.by || "a user"}` : "daily job"})
                  </span>
                )}
              </div>
              {data.sync.needed === null ? (
                <div className="text-warning fw-600" data-testid="sync-other-dates">
                  These dates are not the ones Sync now uses. Sync now and the daily job use the last {data.sync.jobRange.totalDays} days ({data.sync.jobRange.startDay} to{" "}
                  {data.sync.jobRange.endDay}, Settings &gt; Report range), so its draft POs can differ from what you see here.{" "}
                  <Button variant="link" className="p-0 fs-14 fw-bold align-baseline" onClick={() => data?.sync && showSyncDates(data.sync.jobRange)} data-testid="sync-show-dates">
                    Show those dates
                  </Button>
                </div>
              ) : data.sync.needed > 0 ? (
                <div className="text-warning fw-600">
                  {data.sync.needed} product(s) changed since the last sync: their draft PO lines need adding, updating or removing.
                </div>
              ) : (
                <div className="text-success fw-600">Draft POs match this report.</div>
              )}
              {syncing && !data.sync.current?.status && <div className="text-primary fw-600 mt-1">Starting the sync...</div>}
              {data.sync.current && !(syncing && data.sync.current.status !== "running") && <SyncRunSummary run={data.sync.current} />}
            </div>
            {data.sync.canSync && (
              <div className="d-flex gap-2">
                {syncing ? (
                  <>
                    <Button variant="warning" disabled style={{ minWidth: 150 }}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {cancelling ? "Cancelling..." : "Syncing..."}
                    </Button>
                    <Button variant="light-danger" onClick={cancelSync} disabled={cancelling}>
                      Cancel sync
                    </Button>
                  </>
                ) : (
                  (data.sync.needed === null || data.sync.needed > 0) && (
                    <Button variant="warning" onClick={syncNow} disabled={loading} style={{ minWidth: 150 }} title={`Uses ${data.sync.jobRange.startDay} to ${data.sync.jobRange.endDay}`}>
                      Sync now ({data.sync.jobRange.totalDays} days)
                    </Button>
                  )
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {data && (
        <Row className="g-4 mb-5">
          {[
            { label: "Rows", value: fmt(data.total) },
            { label: "Qty Sold", value: fmt(data.summary.qtySold) },
            { label: "Sales Value (TSh)", value: fmt(data.summary.salesValue) },
            { label: "Revenue Loss (TSh)", value: fmt(data.summary.revenueLoss) },
            { label: "Products to Procure", value: fmt(data.summary.orderNeededRows) },
          ].map((c) => (
            <Col key={c.label}>
              <Card className="border h-100">
                <Card.Body className="py-4 px-6">
                  <div className="fs-14 text-gray-600">{c.label}</div>
                  <div className="fs-20 fw-bolder">{c.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card className="border position-relative">
        {loading && records.length > 0 && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-20"
            style={{ background: "rgba(255,255,255,0.7)", zIndex: 5 }}
          >
            <div className="d-flex align-items-center gap-3 bg-white border rounded px-6 py-4 shadow-sm fs-15 fw-600">
              <Spinner animation="border" variant="primary" />
              Loading report... a full year can take up to 20 seconds
            </div>
          </div>
        )}
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <table className="table table-row-bordered align-middle gs-5 gy-3 mb-0 fs-14">
              <thead className="bg-light position-sticky top-0" style={{ zIndex: 1 }}>
                <tr className="fw-bold text-dark">
                  {COLUMNS.filter((c) => c.key !== "orderNeededUnit").map((c) => (
                    <th
                      key={c.key}
                      className={`text-nowrap ${c.sortable ? "cursor-pointer" : ""}`}
                      onClick={() => c.sortable && handleSort(c.key)}
                    >
                      {c.label}
                      {sortKey === c.key ? (sortOrder === 1 ? " ▲" : " ▼") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && !records.length ? (
                  <tr>
                    <td colSpan={COLUMNS.length - 1} className="text-center py-10">
                      <Spinner animation="border" className="me-3" />
                      Calculating the report (a full year can take a few seconds)...
                    </td>
                  </tr>
                ) : !records.length ? (
                  <tr>
                    <td colSpan={COLUMNS.length - 1} className="text-center py-10 text-gray-600">
                      {selectedWarehouse?.value ? "No products match these filters" : "Choose a warehouse (top right) to load the report"}
                    </td>
                  </tr>
                ) : (
                  records.map((r) => (
                    <tr key={`${r.variant}_${r.type}`}>
                      <td style={{ minWidth: 240 }}>
                        <span
                          role="button"
                          className="fw-600 text-primary text-hover-dark text-decoration-underline"
                          title="Open product details"
                          onClick={() => navigate("/all-products/product-details", { state: { id: r.variant } })}
                        >
                          {r.title}
                        </span>
                        {r.refKey && <div className="fs-12 text-gray-600">{r.refKey}</div>}
                      </td>
                      <td className="text-nowrap" title={partsText(r)}>
                        {qtyLabel(r.type)}
                        {r.parts.some((p) => p.type !== r.type) && (
                          <div className="fs-12 text-gray-600">
                            incl. {r.parts.filter((p) => p.type !== r.type).map((p) => qtyLabel(p.type)).join(", ")}
                          </div>
                        )}
                      </td>
                      <td>{fmt(r.blockedStock, 2)}</td>
                      <td>{fmt(r.totalWarehouseStock, 2)}</td>
                      <td>{fmt(r.qtySold, 2)}</td>
                      <td>{fmt(r.salesValue)}</td>
                      <td className={r.ats <= 0 ? "text-danger fw-bold" : ""}>{fmt(r.ats, 2)}</td>
                      <td>{fmt(r.runRate, 2)}</td>
                      <td>{r.previousRate === null ? <span className="text-gray-600">-</span> : fmt(r.previousRate, 2)}</td>
                      <td>{r.lastYearRate === null ? <span className="text-gray-600">-</span> : fmt(r.lastYearRate, 2)}</td>
                      <td
                        className={r.forecastRate < r.runRate ? "text-warning fw-bold" : ""}
                        title={
                          r.forecastRate < r.runRate
                            ? `Lower than the run rate: ${r.spikeDaysTrimmed > 0 ? `${r.spikeDaysTrimmed} spike day(s) trimmed` : ""}${r.flags.includes("DEMAND_CAPPED") ? `${r.spikeDaysTrimmed > 0 ? "; " : ""}capped vs previous period / last year` : ""}`
                            : "Demand per day used for Order Needed"
                        }
                      >
                        {fmt(r.forecastRate, 2)}
                      </td>
                      <td>{r.stockCoverDays === null ? <span className="text-gray-600">No sales</span> : fmt(r.stockCoverDays, 1)}</td>
                      <td>{fmt(r.oosDays)}</td>
                      <td>{fmt(r.daysInStock)}</td>
                      <td>{fmt(r.revenueLoss)}</td>
                      <td className="text-nowrap">{r.lastRestockDate ?? "-"}</td>
                      <td>{fmt(r.lastRestockQty, 2)}</td>
                      <td
                        title={r.openPos
                          .map((p) => `${p.refKey}: ${p.status}, ${p.ageDays} days old, ${p.pending} pending${p.counted ? "" : " (too old: not counted)"}`)
                          .join("\n")}
                      >
                        {fmt(r.openPoQty, 2)}
                        {r.openPos.length > 0 && (
                          <div className="fs-12 text-gray-600 text-nowrap">
                            {r.openPos.map((p) => (
                              <span key={p.refKey} className={`me-1 ${p.counted ? "" : "text-decoration-line-through text-danger"}`}>
                                {p.refKey}
                              </span>
                            ))}
                          </div>
                        )}
                        {r.oldOpenPoQty > 0 && <div className="fs-12 text-danger text-nowrap">{fmt(r.oldOpenPoQty, 2)} on old POs, not counted</div>}
                      </td>
                      <td className="text-end">{fmt(r.openPoValue)}</td>
                      <td>{fmt(r.leadTimeDays)}</td>
                      <td>{fmt(r.doih)}</td>
                      <td className={`text-nowrap ${r.orderNeeded > 0 ? "fw-bolder text-primary" : ""}`} title={`In ${qtyLabel(r.type)} (qty type of the last completed PO)`}>
                        {fmt(r.orderNeeded)} <span className="fs-12 text-gray-600 fw-600">{UNIT_SHORT[r.type]}</span>
                      </td>
                      <td className="text-nowrap">{r.moq === null ? "-" : `${fmt(r.moq)} ${UNIT_SHORT[r.type]}`}</td>
                      <td
                        className={`text-nowrap ${r.suggestedOrder > 0 ? "fw-bolder" : ""} ${r.suggestedOrder > (r.roundedOrder ?? r.orderNeeded) ? "text-warning" : r.suggestedOrder > 0 ? "text-primary" : ""}`}
                        title={
                          r.suggestedOrder > (r.roundedOrder ?? r.orderNeeded)
                            ? "Raised to the supplier MOQ"
                            : r.suggestedOrder !== r.orderNeeded
                              ? `Order Needed ${fmt(r.orderNeeded)} rounded to the nearest 10; the draft PO orders this`
                              : "Quantity the draft PO will order"
                        }
                      >
                        {fmt(r.suggestedOrder)} <span className="fs-12 text-gray-600 fw-600">{UNIT_SHORT[r.type]}</span>
                      </td>
                      <td style={{ minWidth: 170 }} title={r.draftNote}>
                        {r.onDraft ? (
                          <>
                            {data?.canOpenDrafts ? (
                              <span
                                role="button"
                                className="fw-bold text-primary text-hover-dark"
                                onClick={() => navigate("/UProcure/purchase-order-management/draft-purchase-order-details", { state: { id: r.onDraft?.draftId } })}
                              >
                                {r.onDraft.refKey}
                              </span>
                            ) : (
                              <span className="fw-bold">{r.onDraft.refKey}</span>
                            )}
                            <span className="ms-1 text-nowrap">
                              {fmt(r.onDraft.quantity)} <span className="fs-12 text-gray-600 fw-600">{UNIT_SHORT[r.onDraft.quantityType]}</span>
                            </span>
                          </>
                        ) : null}
                        {r.draftStatus !== "not_needed" && (
                          <div>
                            <span className="badge fw-semibold" style={badgeStyle(DRAFT_STATUS[r.draftStatus].color)}>{DRAFT_STATUS[r.draftStatus].label}</span>
                          </div>
                        )}
                        {r.draftNote && <div className="fs-12 text-gray-600">{r.draftNote}</div>}
                      </td>
                      <td style={{ minWidth: 180 }}>{r.vendorName || "-"}</td>
                      <td>{fmt(r.poStockCount)}</td>
                      <td>{fmt(r.poPurchaseAmount)}</td>
                      <td style={{ minWidth: 220 }}>
                        {r.flags.map((f) => (
                          <span key={f} className="badge fw-semibold me-1 mb-1 text-wrap text-start" style={badgeStyle(FLAGS[f]?.color ?? "secondary")} title={FLAGS[f]?.help}>
                            {FLAGS[f]?.label ?? f}
                            {OLD_PO_FLAG_STATUS[f] &&
                              `: ${r.openPos
                                .filter((p) => !p.counted && p.status === OLD_PO_FLAG_STATUS[f])
                                .map((p) => `${p.refKey} (${p.ageDays} days)`)
                                .join(", ")}${data?.oldPoDays ? `, older than ${data.oldPoDays} days, not counted` : ""}`}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {total > PAGE_SIZE && (
        <div className="mt-5">
          <SimplePagination currentPage={page} onPageChange={setPage} hasMore={page * PAGE_SIZE < total} loading={loading} />
        </div>
      )}
    </>
  );
};

export default InventoryReplenishmentReportPage;
